/**
 * Button color utilities
 *
 * Provides dynamic button colors from API config with fallback to default colors
 */

import type React from "react"

import { ApiConfigResponse } from "@/definitions/types/api/config"

// Default button colors (fallback)
export const DEFAULT_BUTTON_COLORS = {
  background: "#22c55e", // green-500 (HSL: 142 76% 36%)
  text: "#fafaf9", // stone-50
}

/**
 * Convert HEX color to HSL values (format: "H S% L%" for CSS custom properties)
 */
export const hexToHSL = (hex: string): string => {
  const cleanHex = hex.replace("#", "")
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/**
 * Get calendar CSS custom properties from API config
 * Overrides --primary and --primary-foreground with API brand colors
 */
export const getCalendarColors = (
  config?: ApiConfigResponse
): React.CSSProperties => {
  const bgColor =
    config?.visualPreferences?.layout?.colors?.buttonConfirmBackground
  const textColor =
    config?.visualPreferences?.layout?.colors?.buttonConfirmText
  if (!bgColor) return {}
  try {
    return {
      "--primary": hexToHSL(bgColor),
      "--primary-foreground": textColor
        ? hexToHSL(textColor)
        : "0 0% 98%",
    } as React.CSSProperties
  } catch {
    return {}
  }
}

/**
 * Convert HEX color to RGB values
 */
const hexToRGB = (hex: string): string => {
  const cleanHex = hex.replace("#", "")
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  return `${r} ${g} ${b}`
}

/**
 * Calculate relative luminance for contrast checking
 */
const getLuminance = (
  r: number,
  g: number,
  b: number
): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255
    return sRGB <= 0.03928
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Determine if text should be dark or light based on background color
 */
const shouldUseDarkText = (hex: string): boolean => {
  const cleanHex = hex.replace("#", "")
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)

  const luminance = getLuminance(r, g, b)
  return luminance > 0.5 // Use dark text on light backgrounds
}

/**
 * Get dynamic button colors from API config
 * Returns CSS custom properties for button styling
 */
export const getButtonColors = (
  config?: ApiConfigResponse
): Record<string, string> => {
  const buttonBgColor =
    config?.visualPreferences?.layout?.colors
      ?.buttonConfirmBackground
  const buttonTextColor =
    config?.visualPreferences?.layout?.colors
      ?.buttonConfirmText

  // Use defaults if no API config
  if (!buttonBgColor) {
    return {
      "--btn-primary-bg": DEFAULT_BUTTON_COLORS.background,
      "--btn-primary-text": DEFAULT_BUTTON_COLORS.text,
      "--btn-primary-hover":
        DEFAULT_BUTTON_COLORS.background,
    }
  }

  try {
    // Convert background color
    const bgRGB = hexToRGB(buttonBgColor)

    // Use provided text color or calculate based on contrast
    let textColor = buttonTextColor
    if (!textColor) {
      textColor = shouldUseDarkText(buttonBgColor)
        ? "#0f172a" // dark text
        : "#f8fafc" // light text
    }
    const textRGB = hexToRGB(textColor)

    // Create hover color (slightly darker/lighter)
    const isDark = !shouldUseDarkText(buttonBgColor)
    const hoverOpacity = isDark ? "0.9" : "0.8"

    return {
      "--btn-primary-bg": `rgb(${bgRGB})`,
      "--btn-primary-text": `rgb(${textRGB})`,
      "--btn-primary-hover": `rgba(${bgRGB} / ${hoverOpacity})`,
    }
  } catch (error) {
    console.error("Error processing button colors:", error)
    return {
      "--btn-primary-bg": DEFAULT_BUTTON_COLORS.background,
      "--btn-primary-text": DEFAULT_BUTTON_COLORS.text,
      "--btn-primary-hover":
        DEFAULT_BUTTON_COLORS.background,
    }
  }
}
